/* lib/recurringConsent.js — согласие на автоматические списания перед оплатой картой.

   Первая оплата российской картой привязывает карту, и дальше тариф продлевается
   списанием без участия человека. Согласие на это раньше было только косвенным
   (галочка «принимаю оферту» при регистрации). Банк в споре «операцию не
   совершал» спрашивает страницу, где плательщик согласился именно на
   автосписания, — поэтому перед уходом в Робокассу показываем условия и
   обязательную галочку (components/patterns/RecurringConsentModal.jsx), а сервер
   записывает момент согласия в Payment.meta и без него платёж не создаёт.

   Здесь — только правила: когда спрашивать и что слать. Их три места на экране
   (тариф в настройках, мастер бизнес-пространства, счёт по ссылке), и копии
   разошлись бы в том, что потом предъявляют банку. Текст условий —
   recurringConsentText.js (ему нужны форматтеры; этот файл без зависимостей,
   потому что его тянет слой API). Заморожено recurringConsent.test.js. */

/* Версия ТЕКСТА согласия. Уходит на сервер вместе с галочкой и ложится в
   Payment.meta.recurring_consent.version — по ней восстанавливают, что именно
   человек видел. Менять при любой правке СМЫСЛА текста в recurringConsentText.js
   и в модалке. Сервер хранит только [0-9A-Za-z._-]{1,32}. */
const RECURRING_CONSENT_VERSION = '2026-10-05';

/* Нужна ли галочка при покупке тарифа из настроек.
   Правило зеркалит сервер (billing.payment_binds_card): карта привязывается у
   продлеваемого тарифа при оплате российской картой. Инокарта рекуррента не
   создаёт. Тариф без поля `renewable` (старый ответ сервера) считаем
   продлеваемым: лишний раз спросить согласие безопаснее, чем не спросить. */
function consentNeededForPlan(plan, method) {
  if (method === 'foreign_card') return false;
  return !(plan && plan.renewable === false);
}

/* Нужна ли галочка при оплате счёта по именной ссылке. `auto_renew` сервер
   отдаёт уже выведенным (решение менеджера × возможности тарифа) — выводить его
   здесь из `plan.renewable` нельзя: разовая продажа обычного тарифа показала бы
   согласие на списания, которых не будет. */
function consentNeededForLink(link, method) {
  if (method === 'foreign_card') return false;
  return !!(link && link.auto_renew);
}

/* Поля тела запроса оплаты. Без согласия — пусто: сервер сам решит, отказать
   или пропустить (инокарта, разовый тариф). */
function consentBody(accepted) {
  return accepted
    ? { recurring_consent: true, recurring_consent_version: RECURRING_CONSENT_VERSION }
    : {};
}

/* Понижение тарифа (17.10.2026): платный тариф дешевле включается оплатой продления
   по ЕГО цене — с привязанной карты спишут другую сумму, возможно, за другой срок.
   Согласие, данное при первой оплате, было на прежнюю цену, поэтому перед
   запланированным переходом — то же окно с галочкой (RecurringConsentModal с
   `startsOn`). Текст у этого окна свой, и версия своя: по ней в споре
   восстанавливают, что человек видел. */
const PLAN_CHANGE_CONSENT_VERSION = '2026-10-17.change';

/* Нужна ли галочка перед понижением: только если следующий период спишут с карты.
   Канал решает сервер (`renewal_channel` в /me/ — то же правило, что у кронов):
   счёт клиент оплачивает сам. Нет поля (старый ответ) — не спрашиваем заранее:
   сервер ответит recurring_consent_required, и экран откроет окно. */
function consentNeededForPlanChange(subscription) {
  return !!(subscription && subscription.renewal_channel === 'card');
}

/* expectedPriceRub — цена нового тарифа, которую человек видел в окне согласия. Сервер
   пишет в согласие цену на момент клика и сверяет её с этой: консоль могла сменить цену,
   пока окно было открыто, — тогда 409 price_changed и ничего не записано (S33).
   Неизвестную цену не шлём: сервер на галочку без цены тоже отвечает 409 price_changed
   с актуальной ценой (`price_rub`), и экран открывает окно заново уже с ней. */
function planChangeConsentBody(accepted, expectedPriceRub) {
  if (!accepted) return {};
  const body = { recurring_consent: true, recurring_consent_version: PLAN_CHANGE_CONSENT_VERSION };
  const price = Number(expectedPriceRub);
  if (expectedPriceRub != null && expectedPriceRub !== '' && Number.isFinite(price) && price > 0) {
    body.expected_price_rub = String(price);
  }
  return body;
}

/* Сервер отказал в переходе: цена тарифа сменилась, пока окно согласия было открыто.
   Экран перечитывает тарифы и показывает окно заново — с новой ценой и пояснением. */
function isPriceChangedError(err) {
  return !!(err && err.code === 'price_changed');
}

/* Сервер отказал в оплате из-за отсутствующего согласия. Страховка на случай,
   если экран решил «согласие не нужно», а сервер считает иначе: тогда не
   показываем человеку тупик, а открываем то же окно с галочкой. */
function isConsentRequiredError(err) {
  return !!(err && err.code === 'recurring_consent_required');
}

export {
  RECURRING_CONSENT_VERSION, consentNeededForPlan, consentNeededForLink,
  consentBody, isConsentRequiredError,
  PLAN_CHANGE_CONSENT_VERSION, consentNeededForPlanChange, planChangeConsentBody,
  isPriceChangedError,
};
